import React, { useCallback, useMemo, useRef } from 'react';
import { cloneDeep, isEqual, shuffle, times } from 'lodash';

import SectionDividers from '@dyl/components/SectionDividers';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import RubricBlock from '@dyl/blocks/RubricBlock';
import { Chip, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ConnectingLinesRenderer } from '@dyl/components/ConnectingLines';
import { Restore as ResetIcon, Shuffle as ShuffleIcon } from '@mui/icons-material';

import { deriveDisplayOrder, isWithin } from './utils';
import Answer from './Answer';
import Node from "@dyl/task-types/Shared/Node";
import Draggable from "react-draggable";

const LEX = 'ABCDEFGH';
const SLOT_COUNT = 8;

const useStyles = makeStyles(theme => ({
    root: {
        maxWidth: theme.breakpoints.values.md,
    },
    contentRoot: {
        justifyContent: "space-between"
    },
    spacerGrid: {
        height: '25%'
    },
    mainGrid: {
        justifyContent: "flex-end",
        width: '100%',
        height: '66%',
        verticalAlign: 'middle',
        alignItems: 'center',
        backgroundColor: '#e4e3e2',
        padding: '16.7% 0'
    },
    answerGrid: {
        width: '100%',
        height: '100%',
        verticalAlign: 'middle',
    },
    chip: {
        color: 'inherit',
        display: 'flex',
        margin: '0 16px',
        backgroundColor: '#f4f3f2'
    },
    adornment: {
        width: '100%',
        height: '100%',
        verticalAlign: 'middle',
        display: 'inline-flex'
    }
}));

export default function MultipleMatchingEditor() {
    const styles = useStyles();
    const [items, setItems] = useTaskField('items');

    const resetOrder = useCallback(() => {
        setItems(prev => prev.map((item, index) => {
            return {
                ...item,
                drop_zone_location: LEX[index],
                answers: item.answers.map(answer => ({
                    ...answer,
                    // The distractor is the last element by default
                    display_order: !answer.is_correct_answer ? SLOT_COUNT : index + 1
                })),
            };
        }));
    }, []);

    const shuffleOrder = useCallback(() => {
        setItems(prev => {
            const oldDisplayOrder = prev.flatMap(x => x.answers.map(y => y.display_order));
            /* Attempt up to 100 times to get a different order to last time.
             * Reduces chance of same order from 1/7! to (1/7!)^100.
             * We use an inner-function to limit scope pollution and keep const-correctness in the outer scope */
            const displayOrder = (() => {
                let order = [];
                for (let attempt = 0; attempt < 100; attempt++) {
                    order = shuffle(times(SLOT_COUNT, i => i + 1));
                    if (!isEqual(order, oldDisplayOrder)) { break; }
                }
                return order;
            })();

            let index = 0;
            return prev.map((item) => {
                return {
                    ...item,
                    answers: item.answers.map(answer => ({
                        ...answer,
                        display_order: displayOrder[index++],
                    })),
                };
            });
        });
    }, []);

    const displayOrder = useMemo(() => {
        return deriveDisplayOrder(items);
    }, [items]);

    return (
        <div className={styles.root}>
            <ConnectingLinesRenderer>
                <SectionDividers>
                    <RubricBlock />
                    <Grid>
                        <Grid container>
                            <>
                                <Grid item xs={12} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                    <div  style={{ flex: 1 }}>
                                        <Typography variant='subtitle1'>
                                            Choices and starting position in the sidebar
                                        </Typography>
                                    </div>

                                    {/* Buttons */}
                                    <Tooltip title='Reset Order' style={{ align: 'right' }}>
                                        <IconButton onClick={resetOrder} tabIndex={-1} size="large">
                                            <ResetIcon />
                                        </IconButton>
                                    </Tooltip>

                                    <Tooltip title='Shuffle Order' style={{ align: 'right' }}>
                                        <IconButton onClick={shuffleOrder} tabIndex={-1} size="large">
                                            <ShuffleIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            </>
                        </Grid>

                        <Grid container className={styles.contentRoot}>
                            <>
                                <Grid item xs={5}>
                                    <Grid className={styles.spacerGrid}>
                                        <Typography variant='caption'>
                                            Set the display order in the test. Drag the handles to move them up and down. Connect each Key to their position within the  illustration (drag the line).
                                        </Typography>
                                    </Grid>

                                    <Slots />
                                </Grid>

                                <Grid item xs={6} className={styles.answerGrid}>
                                    {/* 8 items */}
                                    {displayOrder.map(item =>
                                        <Answer
                                            list={displayOrder}
                                            key={item?.displayOrder}
                                            index={item?.index}
                                            dropZoneLocationIndex={LEX.indexOf(item?.dropZoneLocation)}
                                            answerIndex={item?.answerIndex}
                                            isDistractor={item?.isDistractor}
                                        />
                                    )}
                                </Grid>
                            </>
                        </Grid>
                    </Grid>
                </SectionDividers>
            </ConnectingLinesRenderer>
        </div>
    );
}

function Slots() {
    const styles = useStyles();
    const [items, setItems] = useTaskField('items');
    // Don't judge me.
    const refs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

    const onStop = (srcIndex) => (event) => {
        const { clientX: x, clientY: y } = event;
        for (let tgtIndex = 0; tgtIndex < refs.length; ++tgtIndex) {
            if (tgtIndex === srcIndex) continue;
            const rect = refs[tgtIndex]?.current?.getBoundingClientRect();
            if (!rect) continue;
            if (isWithin(rect, { x, y })) {
                swap(srcIndex, tgtIndex);
            }
        }
    };

    const swap = (srcIndex, tgtIndex) => {
        const src = items.find(x => x.drop_zone_location === LEX[srcIndex]);
        const tgt = items.find(x => x.drop_zone_location === LEX[tgtIndex]);
        const prev = cloneDeep(items);
        if (tgt) {
            prev[tgt.index].drop_zone_location = src.drop_zone_location;
        }
        prev[src.index].drop_zone_location = LEX[tgtIndex];
        setItems(prev);
    };

    return (
        <Grid className={styles.mainGrid} container>
            {times(SLOT_COUNT, i =>
                <Slot
                    key={i}
                    ref={refs[i]}
                    dropZoneIndex={i}
                    onStop={onStop(i)}
                />)}
        </Grid>
    );
}


const Slot = React.forwardRef((props, ref) => _Slot({ ...props, ref }));

function _Slot({ ref, dropZoneIndex, onStop }) {
    const styles = useStyles();

    return (
        <Grid item md={4} ref={ref}>
            <Chip
                className={styles.chip}
                variant="outlined"
                label={(
                    <Draggable
                        position={{ x: 0, y: 0 }}
                        onStop={onStop}
                    >
                        <Grid className={styles.adornment} style={{ cursor: 'grab' }}>
                            <Node lineId={dropZoneIndex} side='start' />
                        </Grid>
                    </Draggable>
                )} />
        </Grid>
    );
}
